<!-- TEMPLATE -->
<template>
  <div class="wrapper">
    <dean-dialog
        :title="`Hoofdproduct van ${payloadData.serviceData.data.name} dienst wijzigen`"
        :icon="switchIcon"
    >
      <dean-load-overlay
          v-if="loading"
      ></dean-load-overlay>
      <dean-notification :is-modal="true">

      </dean-notification>

      <perfect-scrollbar
          :options="{suppressScrollX:true}"
          class="small-12 medium-12 large-12 hd-12 columns">
        <dean-select-form-row
            v-if="agreementOptions.length > 1"
            id="partnerAgreement"
            label="Overeenkomst"
            placeholder="Kies een overeenkomst"
            :options="agreementOptions"
            :value="currentlySelectedAgreement()"
            :required-field="true"
            :warning="$v.selectedAgreementId.$invalid"
            :clearable="true"
            @input="onAgreementSelected($event)"
        />

        <dean-select-form-row
            v-if="agreementOptions.length <= 1 || selectedAgreementId"
            id="product" label="Product" v-model="selectedProduct" :required-field="true"
                              placeholder="Kies een product" :options="data"
                              :warning="$v.selectedProduct.$invalid"
                              clearable/>

        <collapse-transition>
          <div v-show="selectedProduct && (agreementOptions.length <= 1 || selectedAgreementId)">
            <div>
              <dean-form-row id="comment" label="Opmerking" v-model="comment" v-if="selectedProduct"></dean-form-row>

              <div class="form-row" v-if="selectedProduct">
                <div class="label">Wijzig prijzen</div>
                <div class="value">
                  <dean-input-switch @checked="customizePrices = !customizePrices" :checked="customizePrices"/>
                </div>
              </div>

              <div class="form-row" v-if="selectedProduct">
                <div class="label">Wijzig startdatum</div>
                <div class="value">
                  <dean-input-switch
                      @checked="customizeStartDate = !customizeStartDate"
                      :checked="customizeStartDate"
                  />
                </div>
              </div>

              <h4>Prijs</h4>
              <transition name="fade" mode="out-in">
                <div v-if="selectedProduct && !customizePrices" key="defaultPrices">
                  <div class="form-row">
                    <div class="label">Aansluitprijs</div>
                    <div class="value">
                      {{
                        new Intl.NumberFormat('nl-NL', {
                          style: 'currency',
                          currency: 'EUR'
                        }).format(prices[selectedProduct.value].connectPrice)
                      }}
                    </div>
                  </div>
                  <div class="form-row">
                    <div class="label">Maandelijks</div>
                    <div class="value">
                      {{
                        new Intl.NumberFormat('nl-NL', {
                          style: 'currency',
                          currency: 'EUR'
                        }).format(prices[selectedProduct.value].recurringPrice)
                      }}
                    </div>
                  </div>
                </div>

                <div v-else-if="selectedProduct && customizePrices" key="customPrices">
                  <h4>Prijzen aanpassen</h4>
                  <div class="form-row">
                    <div class="label">Aansluitprijs</div>
                    <div class="value">
                      <dean-form-row
                          type="number"
                          step="0.01"
                          :label="`Standaard ${new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(prices[selectedProduct.value].connectPrice)}`"
                          v-model="customPrices.connectPrice"
                          hide-label="true"
                      />
                    </div>
                  </div>

                  <div class="form-row">
                    <div class="label">Maandelijks</div>
                    <div class="value">
                      <dean-form-row
                          type="number"
                          step="0.01"
                          :label="`Standaard ${new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(prices[selectedProduct.value].recurringPrice)}`"
                          v-model="customPrices.recurringPrice"
                          hide-label="true"
                      />
                    </div>
                  </div>
                </div>
              </transition>

              <transition name="fade" mode="out-in" v-if="selectedProduct && customizeStartDate">
                <div>
                  <h4>Startdatum aanpassen</h4>
                  <date-input
                      label="Gewenste startdatum"
                      placeholder="Gewenste startdatum"
                      :value="customStartDate"
                      :min-date="productMinDate"
                      :max-date="productMaxDate"
                      :warning="$v.customStartDate.$error"
                      @dateValueChanged="wishDateValueChanged"
                  ></date-input>
                </div>
              </transition>
            </div>
          </div>
        </collapse-transition>
      </perfect-scrollbar>

      <span slot="dean-dialog-button-group">
        <dean-button :danger="true" :dialog-button="true" @click.native="close()">Sluiten</dean-button>
        <dean-button :success="true" :dialog-button="true"
                     @click.native="processOrder">Bestellen</dean-button>
      </span>
    </dean-dialog>
  </div>
</template>

<!-- SCRIPT -->
<script lang="ts">
import Vue from 'vue';
import {Component, Prop, Watch} from 'vue-property-decorator';
import {
  // SVG
  warningIcon,
  // COMPONENTS
  deanDialog,
  switchIcon,
  orderIcon,
  deanTable,
  deanLoadOverlay,
} from '@deanone/dean-components';
import deanButton from '@/components/deanComponents/deanButton.vue';
import DeanNotification from '@/components/deanComponents/deanNotification.vue';
import DeanSelectFormRow from '@/components/deanComponents/deanSelectFormRow.vue';
import DeanFormRow from '@/components/deanComponents/deanFormRow.vue';
import deanAccordion from '@/components/deanComponents/deanAccordion.vue';
import DeanInputSwitch from '@/components/deanComponents/deanInputSwitch.vue';
import DeanInputField from '@/components/deanComponents/deanInputField.vue';
import WishDate from '@/components/quote/wishDate.vue';
import {BTable} from 'bootstrap-vue';
import {deanDialogBus} from '@/main';
import {CollapseTransition} from 'vue2-transitions';
import {required, requiredIf} from 'vuelidate/lib/validators';
import {Validations} from 'vuelidate-property-decorators';
import axios from 'axios';
import {getErrorCodeFromResponse, getErrorMessageFromResponse, getErrorMessageFromTranslationByErrorCode} from '@/mixins/errorMessage';
import DateInput from '@/components/DateInput.vue';
import {wishDateValidator} from '@/mixins/validators';

@Component({
  components: {
    DeanInputField,
    DeanFormRow,
    deanLoadOverlay,
    deanAccordion,
    warningIcon,
    switchIcon,
    DeanNotification,
    DeanSelectFormRow,
    deanDialog,
    deanButton,
    DeanInputSwitch,
    orderIcon,
    CollapseTransition,
    deanTable,
    BTable,
    WishDate,
    DateInput,
  },
})

export default class ServiceChangeProduct extends Vue {

  // COMPUTED
  @Validations()
  get validations() { // TODO
    const validations: any = {
      selectedProduct: {
        required,
      },
      selectedAgreementId: {
        required: requiredIf(() => this.agreementOptions.length > 1),
      },
      customizePrices: {
        required,
      },
      additionalData: {
        $each: this.additionalDataValidations,
      },
      // Required if default price is used
      prices: {},
      // Required if custom prices are entered
      customPrices: {
        connectPrice: {
          required: requiredIf(() => {
            return this.customizePrices;
          }),
        },
        recurringPrice: {
          required: requiredIf(() => {
            return this.customizePrices;
          }),
        },
      },
      customStartDate: {
        required: requiredIf(() => {
          return this.customizeStartDate;
        }),
        wishDateValidator: wishDateValidator(
            this.productMinDate ? this.productMinDate.toDateString() : null,
            this.productMaxDate ? this.productMaxDate.toDateString() : null
        )
      },
    };

    if (this.selectedProduct) {
      validations.prices[this.selectedProduct.value] = {
        connectPrice: {
          required: requiredIf(() => {
            return !this.customizePrices;
          }),
        },
        recurringPrice: {
          required: requiredIf(() => {
            return !this.customizePrices;
          }),
        },
      };
    }

    return validations;
  }

  get condition() {
    return (condition: string | number) => {
      return this.additionalDataFields[condition] === true;
    };
  }
  // DATA
  @Prop() public payloadData!: {
    [key: string]: any,
  };
  public data: any = [];
  public rules: any = [];
  public comment: string = '';
  public prices: any = {};
  public customPrices: any = {};
  public customStartDate: any = null;
  public fields: any = [];
  public addresses: any = [];
  public additionalDataFields: any = {};
  public additionalData: any = [];
  public switchIcon: any = switchIcon;
  public loading: boolean = false;
  public customizePrices: boolean = false;
  public customizeStartDate: boolean = false;
  public selectedProduct: any = null;
  public additionalDataValidations = {};
  public partnerCode: string = '';
  public agreementOptions: Array<{ value: string, label: string }> = [];
  public selectedAgreementId: string | null = null;
  private productMinDate: null|Date = null;
  private productMaxDate: null|Date = null;

  @Watch('selectedProduct')
  public checkFields() {
    if (!this.selectedProduct) {
      this.customPrices = {};

      return;
    }

    this.customPrices = {...this.prices[this.selectedProduct.value]};
  }

  public wishDateValueChanged(data: string) {
    this.customStartDate = data;
  }

  // METHODS
  public close() {
    deanDialogBus.$emit('close');
  }

  public currentlySelectedAgreement() {
    if (this.selectedAgreementId !== null) {
      const found = this.agreementOptions.find((o) => o.value === this.selectedAgreementId);
      return found || null;
    }
    return null;
  }

  public processOrder() {
    this.loading = true;
    this.$v.$touch();
    if (!this.$v.$invalid) {
      const partnerAgreementId = this.agreementOptions.length === 1
        ? this.agreementOptions[0].value
        : this.selectedAgreementId;
      axios.post('/-/trade/api/customer.service.changeProduct.admin.order', {
        serviceId: this.payloadData.serviceData.data.id,
        customerId: this.$route.params.pid,
        productId: this.selectedProduct.value,
        partnerAgreementId,
        prices: this.customizePrices ? this.customPrices : this.prices[this.selectedProduct.value],
        startDate: this.customizeStartDate ? this.customStartDate : null,
        comment: this.comment ? this.comment : null,
      }).then(
          (response) => {
            this.$toast({
              icon: orderIcon,
              title: 'Order aangemaakt',
              text: 'Uw order is aangemaakt.',
              theme: 'success',
            });

            this.$router.push({name: 'customers.orders.list', params: {
              pid: this.$route.params.pid,
            }});

            deanDialogBus.$emit('close');
          },
          (error) => {
            const errorResponse = error.response.data;
            const errorCode: string|null = getErrorCodeFromResponse(errorResponse);
            let errorMessage: string|null|any = null;

            if (errorCode) {
              errorMessage = getErrorMessageFromTranslationByErrorCode(errorCode);
            }

            if (null === errorMessage) {
              errorMessage = getErrorMessageFromResponse(errorResponse, this.$store.state.main.notification.messages.couldNotSendData.text);
            }

            if (errorMessage) {
              this.$toast({
                icon: warningIcon,
                title: 'Waarschuwing',
                text: errorMessage,
                theme: 'warning',
              });
            } else {
              this.$toast(this.$store.state.main.notification.messages.couldNotSendData);
            }
          },
      ).finally(
          () => {
            this.loading = false;
          },
      );
    } else {
      this.$toast({
        icon: warningIcon,
        title: 'Waarschuwing',
        text: 'Onjuist verzoek. Zorg ervoor dat alle velden zijn ingevuld en probeer het nogmaals.',
        theme: 'warning',
      });
      this.loading = false;
    }
  }

  public created() {
    this.loading = true;

    axios.post('/-/trade/api/customer.partner.list', {
      id: this.$route.params.pid,
    }).then(
      (response) => {
        const partners = response.data;
        if (partners.length > 0) {
          const partnerId = partners[0].value;
          return axios.post('/-/trade/api/customer.partner.agreements.list', {
            id: partnerId,
            activeOnly: false,
          });
        }
      },
    ).then(
      (agreementsResponse: any) => {
        if (agreementsResponse) {
          const agreements = agreementsResponse.data.data;
          this.agreementOptions = agreements.map((agreement: any) => ({
            value: agreement.id,
            label: agreement.description + ' (' + agreement.typeName + ')',
          }));
        }
        if (this.agreementOptions.length <= 1) {
          this.loadChangeProductInfo();
        } else {
          this.loading = false;
        }
      },
      (error) => {
        this.$toast(this.$store.state.main.notification.messages.couldNotReceiveData);
        this.loading = false;
      },
    );
  }

  public loadChangeProductInfo(partnerAgreementId?: string) {
    this.loading = true;
    const payload: any = {
      serviceId: this.payloadData.serviceData.data.id,
      customerId: this.$route.params.pid,
    };
    if (partnerAgreementId) {
      payload.partnerAgreementId = partnerAgreementId;
    }
    axios.post('/-/trade/api/customer.service.changeProduct.info', payload).then(
        (response) => {
          this.data = response.data.changeProducts.map((product: any) => {
            return {
              label: product.product.publicName,
              value: product.product.id,
            };
          });
          if (!response.data.changeProducts.length && !partnerAgreementId) {
            this.$toast({
              icon: warningIcon,
              title: 'Waarschuwing',
              text: 'Geen uitbreidingen gevonden voor het gekozen product.',
              theme: 'warning',
            });
            deanDialogBus.$emit('close');
          }

          this.rules = response.data.rules;
          response.data.changeProducts.forEach((product: any) => {
            this.prices[product.product.id] = {
              connectPrice: product.price.connectPrice,
              recurringPrice: product.price.recurringPrice,
            };
          });

          this.partnerCode = response.data.partnerCode;

          this.addresses = response.data.addresses.map((address: any) => {
            return {
              label: address.label,
              value: address.id,
            };
          });
        },

        (error) => {
          this.$toast(this.$store.state.main.notification.messages.couldNotReceiveData);
        },
    ).finally(
        () => {
          this.loading = false;
        },
    );
  }

  public onAgreementSelected(option: any) {
    this.selectedAgreementId = option ? option.value : null;
    this.selectedProduct = null;
    if (this.selectedAgreementId) {
      this.loadChangeProductInfo(this.selectedAgreementId);
    }
  }

  @Watch('customizeStartDate')
  private getMinAndMaxDateForProduct(): void {
    if (this.selectedProduct && this.selectedProduct.value) {
      axios
        .post('/-/trade/api/productWishDate.info', {productId: this.selectedProduct.value})
        .then(
            (response: any) => {
              this.productMinDate = response.data.minDate ? new Date(response.data.minDate) : null;
              this.productMaxDate = response.data.maxDate ? new Date(response.data.maxDate) : null;
            },
            (error: any) => {
              // tslint:disable-next-line:no-console
              console.error(error);
            },
        );
    }
  }
}
</script>

<!-- STYLE -->
<style lang="scss" scoped>
// IMPORT
@import '~@deanone/dean-components/src/scss/vars';

:deep(.dialog .dean-dialog-container) {
  width: 600px;
}

.list-enter-active, .list-leave-active {
  transition: all 1s;
}

.list-enter, .list-leave-to /* .list-leave-active below version 2.1.8 */
{
  opacity: 0;
  transform: translateY(30px);
}

.form-row {
  display: flex;

  & > div {
    flex-grow: 1;
    width: 50%;

    &.label {
      align-self: center;
      padding: 10px 10px 10px 25px;
    }

    &.value {
      padding: 10px 10px 10px 0;
    }
  }
}

.field {
  display: flex;
  align-items: center;

  .label {
    padding-left: 10px;
    flex-grow: 1;
    width: 50%;
  }

  .value {
    flex-grow: 1;
    width: 50%;
  }
}

.dean-table table td {
  height: auto !important;
}
</style>

<style lang="scss">
.vs__dropdown-menu {
  width: auto !important;
}

.v-select__selections input {
  display: none
}
</style>
